import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const BiasDetectorViewResult = ({result}) => {
  const getSeverityColor = (severity) => {
    switch(severity?.toLowerCase()) {
      case 'high':
        return 'bg-error/20 text-error';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-success/20 text-success';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const getScoreColor = (score) => {
    if (score <= 0.3) return 'bg-error/20 text-error';
    if (score <= 0.6) return 'bg-warning/20 text-warning';
    return 'bg-success/20 text-success';
  };

  // Helper to render score data handling different types
  const renderScoreData = (scoreData) => {
    if (!scoreData) return null;

    // Handle string/number
    if (typeof scoreData === 'string' || typeof scoreData === 'number') {
      return (
        <span className={`px-2 py-0.5 rounded-full text-xs ${getScoreColor(Number(scoreData))}`}>
          {typeof scoreData === 'number' ? Math.round(scoreData * 100) + '%' : scoreData}
        </span>
      );
    }

    // Handle array
    if (Array.isArray(scoreData)) {
      return (
        <div className="space-y-2">
          {scoreData.map((item, index) => (
            <div key={index} className="flex items-center gap-2 p-2 bg-base-300 rounded-lg">
              <PhosphorIcons.CaretRight size={16} className="text-primary"/>
              <span className="text-sm">{item}</span>
            </div>
          ))}
        </div>
      );
    }

    // Handle object
    if (typeof scoreData === 'object') {
      return (
        <div className="space-y-2">
          {Object.entries(scoreData).map(([key, value]) => (
            <div key={key} className="flex justify-between items-center">
              <span className="font-medium capitalize">{key.replace(/_/g, ' ')}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${getScoreColor(Number(value))}`}>
                {typeof value === 'number' ? Math.round(value * 100) + '%' : value}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Bias Analysis Section */}
        <div className="space-y-4">
          {/* Overall Score Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Overall Bias Scores</h3>
              </div>

              <div className="space-y-3">
                {result?.bias_analysis?.overall_score && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    {renderScoreData(result.bias_analysis.overall_score)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Detected Biases Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.Warning size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Detected Biases</h3>
              </div>

              <div className="space-y-3">
                {result?.bias_analysis?.detected_biases?.map((bias, index) => (
                  <div key={index} className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium capitalize">{bias.type?.replace(/_/g, ' ')}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getSeverityColor(bias.severity)}`}>
                        {bias.severity}
                      </span>
                    </div>
                    {bias.evidence && (
                      <div className="text-sm text-base-content/70 mb-2">
                        Evidence: {bias.evidence}
                      </div>
                    )}
                    {bias.suggestion && (
                      <div className="flex items-center gap-2 p-2 bg-info/10 rounded-lg text-sm">
                        <PhosphorIcons.LightbulbFilament size={16} className="text-info"/>
                        <span>{bias.suggestion}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Metadata Section */}
        <div className="space-y-4">
          {/* Analysis Quality Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.Gauge size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Analysis Quality</h3>
              </div>

              <div className="space-y-3">
                {/* Analysis Quality */}
                {result?.metadata?.analysis_quality && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Quality</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-info/20 text-info capitalize">
                        {result.metadata.analysis_quality}
                      </span>
                    </div>
                  </div>
                )}

                {/* Confidence Score */}
                {result?.metadata?.confidence_score != null && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">Confidence</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                        {Math.round(result.metadata.confidence_score * 100)}%
                      </span>
                    </div>
                    <div className="w-full bg-base-300 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full bg-success"
                        style={{width: `${result.metadata.confidence_score * 100}%`}}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BiasDetectorViewResult;
